import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, config, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import Partenaire from 'app/components/partenaire/partenaire.model';
import { clubConfig } from 'config/club.config';
import { environment } from 'environments/environment';

@Injectable({
    providedIn: 'root',
})
export class SponsorService {
    private apiBaseUrl = `${environment.apiNest_2}/api/v1/sponsors`;

    constructor(private http: HttpClient) {}

    /**
     * Récupère tous les sponsors
     */
    getSponsors(): Observable<Partenaire[]> {
        return this.http.get<any[]>(`${this.apiBaseUrl}`).pipe(
            map((sponsors) => this.shuffleArray(sponsors)), // Mélanger les sponsors
            catchError((error) => {
                console.error(
                    'Erreur lors de la récupération des sponsors:',
                    error
                );
                return throwError(
                    () =>
                        new Error(
                            'Impossible de récupérer les sponsors. Veuillez réessayer plus tard.'
                        )
                );
            })
        );
    }

    /**
     * Récupère un sponsor par son ID
     * @param id ID du sponsor
     */
    getSponsorById(id: string): Observable<Partenaire> {
        return this.http.get<any>(`${this.apiBaseUrl}/${id}`).pipe(
            catchError((error) => {
                console.error(
                    `Erreur lors de la récupération du sponsor ${id}:`,
                    error
                );
                return throwError(
                    () =>
                        new Error(
                            `Impossible de récupérer le sponsor. Veuillez réessayer plus tard.`
                        )
                );
            })
        );
    }

    /**
     * Récupère les sponsors associés à un club spécifique
     * @param clubId ID du club
     */
    getSponsorsByClub(clubId: string): Observable<Partenaire[]> {
        return this.http.get<any[]>(`${this.apiBaseUrl}/club/${clubId}`).pipe(

            map((sponsors) => this.shuffleArray(sponsors)), // Mélanger les sponsors
            catchError((error) => {
                console.error(
                    `Erreur lors de la récupération des sponsors du club ${clubId}:`,
                    error
                );
                return throwError(
                    () =>
                        new Error(
                            'Impossible de récupérer les sponsors du club. Veuillez réessayer plus tard.'
                        )
                );
            })
        );
    }

    /**
     * Récupère les sponsors équipementiers associés à un club spécifique
     * @param clubId ID du club
     */
    getEquipmentSponsorsByClub(clubId: string): Observable<Partenaire[]> {
        return this.http
            .get<any[]>(`${this.apiBaseUrl}/equipment/club/${clubId}`)
            .pipe(
                map((sponsors) => this.shuffleArray(sponsors)), // Mélanger les sponsors
                catchError((error) => {
                    console.error(
                        `Erreur lors de la récupération des sponsors équipementiers du club ${clubId}:`,
                        error
                    );
                    return throwError(
                        () =>
                            new Error(
                                'Impossible de récupérer les sponsors équipementiers du club. Veuillez réessayer plus tard.'
                            )
                    );
                })
            );
    }

    /**
     * Récupère les sponsors principaux
     */
    getMainSponsors(): Observable<Partenaire[]> {
        return this.http.get<any[]>(`${this.apiBaseUrl}/main`).pipe(
            map((sponsors) => this.shuffleArray(sponsors)), // Mélanger les sponsors
            catchError((error) => {
                console.error(
                    'Erreur lors de la récupération des sponsors principaux:',
                    error
                );
                return throwError(
                    () =>
                        new Error(
                            'Impossible de récupérer les sponsors principaux. Veuillez réessayer plus tard.'
                        )
                );
            })
        );
    }

    /**
     * Recherche des sponsors selon différents critères
     * @param params Paramètres de recherche
     */
    searchSponsors(params: {
        clubId?: string;
        isMainSponsor?: boolean;
        equipmentOnly?: boolean;
        searchTerm?: string;
    }): Observable<Partenaire[]> {
        // Construire les paramètres de requête
        const queryParams = new URLSearchParams();
        if (params.clubId) queryParams.append('clubId', params.clubId);
        if (params.isMainSponsor !== undefined)
            queryParams.append(
                'isMainSponsor',
                params.isMainSponsor.toString()
            );
        if (params.equipmentOnly !== undefined)
            queryParams.append(
                'equipmentOnly',
                params.equipmentOnly.toString()
            );
        if (params.searchTerm)
            queryParams.append('searchTerm', params.searchTerm);

        return this.http
            .get<any[]>(`${this.apiBaseUrl}/search?${queryParams.toString()}`)
            .pipe(
                map((sponsors) => this.shuffleArray(sponsors)), // Mélanger les sponsors
                catchError((error) => {
                    console.error(
                        'Erreur lors de la recherche des sponsors:',
                        error
                    );
                    return throwError(
                        () =>
                            new Error(
                                'Impossible de rechercher les sponsors. Veuillez réessayer plus tard.'
                            )
                    );
                })
            );
    }

    /**
     * Crée un nouveau sponsor
     * @param sponsor Les données du sponsor à créer
     */
    createSponsor(sponsor: Partenaire): Observable<Partenaire> {
        return this.http
            .post<any>(`${this.apiBaseUrl}`, sponsor, {
                headers: {
                    'x-club-id': clubConfig[0].id,
                },
            })
            .pipe(
                catchError((error) => {
                    console.error(
                        'Erreur lors de la création du sponsor:',
                        error
                    );
                    return throwError(
                        () =>
                            new Error(
                                'Impossible de créer le sponsor. Veuillez réessayer plus tard.'
                            )
                    );
                })
            );
    }

    /**
     * Met à jour un sponsor existant
     * @param id ID du sponsor à mettre à jour
     * @param sponsor Les données mises à jour du sponsor
     */
    updateSponsor(id: string, sponsor: Partenaire): Observable<Partenaire> {
        return this.http.patch<any>(`${this.apiBaseUrl}/${id}`, sponsor).pipe(
            catchError((error) => {
                console.error(
                    `Erreur lors de la mise à jour du sponsor ${id}:`,
                    error
                );
                return throwError(
                    () =>
                        new Error(
                            'Impossible de mettre à jour le sponsor. Veuillez réessayer plus tard.'
                        )
                );
            })
        );
    }

    /**
     * Supprime un sponsor
     * @param id ID du sponsor à supprimer
     */
    deleteSponsor(id: string): Observable<any> {
        return this.http.delete<any>(`${this.apiBaseUrl}/${id}`).pipe(

            catchError((error) => {
                console.error(
                    `Erreur lors de la suppression du sponsor ${id}:`,
                    error
                );
                return throwError(
                    () =>
                        new Error(
                            'Impossible de supprimer le sponsor. Veuillez réessayer plus tard.'
                        )
                );
            })
        );
    }

    /**
     * Télécharge une image pour un sponsor
     * @param file Le fichier image à télécharger
     */
    uploadImage(file: File): Observable<string> {
        // Création du FormData
        const formData = new FormData();
        formData.append('files', file);

        // Paramètres pour l'URL
        const params = new HttpParams()
            .set('source', 'sponsors')
            .set('typeMedia', 'image')
            .set('isRemoveBackground', 'false')
            .set('isLimitFileUploads', 'false');

        return this.http
            .post<any>(
                `${environment.apiNest_2}/api/v1/upload/service`,
                formData,
                { params }
            )
            .pipe(
                map((response) => {
                    // On suppose que le service renvoie un tableau d'URLs
                    if (Array.isArray(response) && response.length > 0) {
                        return response[0]; // Première URL
                    }
                    throw new Error(
                        "Aucune URL d'image retournée par le serveur"
                    );
                }),
                tap((url) => console.log('API - Image téléchargée:', url)),
                catchError((error) => {
                    console.error(
                        "Erreur lors du téléchargement de l'image:",
                        error
                    );
                    return throwError(
                        () =>
                            new Error(
                                "Impossible de télécharger l'image. Veuillez réessayer plus tard."
                            )
                    );
                })
            );
    }

    /**
     * Supprime une image de sponsor du serveur
     * @param imageUrl URL de l'image à supprimer
     */
    deleteImageFromServer(imageUrl: string): Observable<any> {
        // Paramètres pour l'URL
        const params = new HttpParams().set('urls', imageUrl);

        return this.http
            .post<any>(
                `${environment.apiNest_2}/api/v1/upload/service/delete-files`,
                {},
                { params }
            )
            .pipe(
                tap(() => console.log('API - Image supprimée du serveur')),
                catchError((error) => {
                    console.error(
                        "Erreur lors de la suppression de l'image du serveur:",
                        error
                    );
                    return throwError(
                        () =>
                            new Error(
                                "Impossible de supprimer l'image du serveur. Veuillez réessayer plus tard."
                            )
                    );
                })
            );
    }
    /**
     * Mélange un tableau de manière aléatoire (algorithme de Fisher-Yates)
     * @param array Le tableau à mélanger
     * @returns Le tableau mélangé
     */
    private shuffleArray<T>(array: T[]): T[] {
        // Créer une copie pour ne pas modifier l'original
        const shuffled = [...array];

        // Algorithme de Fisher-Yates
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        return shuffled;
    }
}
